import {
  canModifyAOIs,
  canResolveNotifications,
  canViewAois,
  canViewAreas,
  canDownloadGeneratedReports,
  canViewDashboard,
  canViewDashboardNotifications,
  canViewDashboardThroughput,
  canViewDensityAnalytics,
  canViewLiveSensors,
  canViewLiveTracks,
  canViewLiveView,
  canViewPopulationAnalytics,
  canViewPlayback,
  canViewSettings,
  canModifySettings,
} from "@/features/deployment-selection/model/permissions";
import type { Deployment } from "@/features/deployment-selection/model/deployment";

/**
 * Verified runtime-api operation catalog.
 *
 * IMPORTANT: Only include an operation here once its `operation_id` is confirmed
 * against `situate-platform-api`'s operations catalog
 * (`@situate/contracts/runtime-api/operations.yaml`). The runtime-session endpoint
 * rejects the *entire* request with `400 unknown_operation` if any requested
 * operation ID is not recognized, so guessing IDs here can break runtime access
 * for a whole deployment. Extend this list only when a new operation ID has been
 * confirmed in `situate-platform-api` docs or tests.
 */
const OPERATION_CATALOG: ReadonlyArray<{
  operationId: string;
  isPermitted: (deployment: Deployment) => boolean;
}> = [
  { operationId: "dashboard_overview", isPermitted: canViewDashboard },
  {
    operationId: "dashboard_area_current_population",
    isPermitted: canViewDashboard,
  },
  { operationId: "live_sensors_list", isPermitted: canViewLiveSensors },
  { operationId: "live_tracks_stream", isPermitted: canViewLiveTracks },
  {
    operationId: "analytics_area_population",
    isPermitted: canViewPopulationAnalytics,
  },
  {
    operationId: "analytics_total_population_trend",
    isPermitted: canViewPopulationAnalytics,
  },
  {
    operationId: "analytics_site_total_population_chart",
    isPermitted: canViewLiveView,
  },
  {
    operationId: "analytics_area_density",
    isPermitted: canViewDensityAnalytics,
  },
  {
    operationId: "analytics_line_throughput",
    isPermitted: canViewDashboardThroughput,
  },
  {
    operationId: "throughput_line_configs_list",
    isPermitted: canViewDashboardThroughput,
  },
  {
    operationId: "queue_configs_list",
    isPermitted: canViewDashboardThroughput,
  },
  {
    operationId: "analytics_queue_metrics",
    isPermitted: canViewDashboardThroughput,
  },
  {
    operationId: "live_queues_current",
    isPermitted: canViewLiveView,
  },

  {
    operationId: "notifications_list",
    isPermitted: canViewDashboardNotifications,
  },
  {
    operationId: "alert_detail",
    isPermitted: canViewDashboardNotifications,
  },
  {
    operationId: "alert_resolve",
    isPermitted: canResolveNotifications,
  },
  {
    operationId: "live_notifications_stream",
    isPermitted: canViewDashboardNotifications,
  },
  { operationId: "areas_list", isPermitted: canViewAreas },
  { operationId: "generated_report_download", isPermitted: canDownloadGeneratedReports },
  { operationId: "playback_manifest", isPermitted: canViewPlayback },
  { operationId: "playback_segment", isPermitted: canViewPlayback },
  { operationId: "settings_areas_list", isPermitted: canViewSettings },
  { operationId: "settings_thresholds_list", isPermitted: canViewSettings },
  { operationId: "settings_areas_update", isPermitted: canModifySettings },
  { operationId: "aois_list", isPermitted: canViewAois },
  { operationId: "aoi_create", isPermitted: canModifyAOIs },
  { operationId: "aoi_update", isPermitted: canModifyAOIs },
  { operationId: "aoi_delete", isPermitted: canModifyAOIs },
];

/**
 * Build the list of runtime-session operation IDs to request for a deployment,
 * based on the operations the user's deployment permissions allow.
 *
 * Returns an empty array if the user has none of the verified permissions,
 * in which case the runtime session should not be requested at all.
 *
 * The server computes resource constraints (area access) from RBAC; the client
 * only requests operation IDs.
 */
export function buildRequestedOperations(deployment: Deployment): string[] {
  return OPERATION_CATALOG.filter(({ isPermitted }) => isPermitted(deployment)).map(
    ({ operationId }) => operationId
  );
}
